import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  Flex,
  Text,
  Switch,
  MenuGroup,
  Divider,
  Input,
  InputGroup,
  Heading,
  InputLeftElement,
  Box,
  Tooltip,
} from '@chakra-ui/react';
import { allIcons, NormalIcon } from '../utils/Icon';
import ActionButtons from '../tabs/ActionButtons';
import UploadComponent from '../import/UploadComponent';
import { TState, useActions } from '../../redux/store';
import DownloadComponent from '../export/DownloadComponent';
import Chip from '../common/Chip';
import { useAllTabs, useCanI } from '../../redux/actions';
import Notifications from './Notifications';
import ActivityLogs from './ActivityLogs';
import { subscribeUser } from '../notifPush/subscribe';

let searchTimeout: NodeJS.Timeout;
export const chipStyle = { position: 'absolute', top: '-3px', right: '-3px' };

const Toolbar = () => {
  const tabs = useSelector((state: TState) => state.tabs);
  const currentTab = useSelector((state: TState) => state.currentTab);
  const tabToUpdate = useSelector((state: TState) => state.tabToUpdate);
  const login = useSelector((state: TState) => state.login);
  const user = useSelector((state: TState) => state.user);
  const isAdmin = useSelector((state: TState) => state.isAdmin);
  const isSuper = useSelector((state: TState) => state.isSuper);
  const isEditMode = useSelector((state: TState) => state.isEditMode);
  const filterActive = useSelector((state: TState) => state.filterActive);
  const isMobile = useSelector((state: TState) => state.isMobile);
  const rowSelected = useSelector((state: TState) => state.rowSelected);
  const globalFilter = useSelector((state: TState) => state.globalFilter);
  const theme = useSelector((state: TState) => state.theme);

  const {
    setCurrentTab,
    setGlobalFilter,
    setUser,
    setModalType,
    setToken,
    toggleFilter,
    onToggle,
    toggleEditMode,
  } = useActions();
  const allTabs = useAllTabs();

  const canI = useCanI();
  const canMulti = canI('multiActions', 'read');
  const canUpload = canI('import', 'read');

  const [globalF, setGlobalF] = useState('');
  const [showActivity, setShowActivity] = useState(false);
  const canViewActivity =
    user.showActivityLogs &&
    `${user._id}` === `${user.client_id}`;

  const handleMode = () => {
    if (currentTab?.isSetting) {
      setCurrentTab(tabs[0]);
    }

    toggleEditMode();
  };

  const handleFilterChange = (e: any) => {
    setGlobalF(e.target?.value || '');
  };

  useEffect(() => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      setGlobalFilter(globalF);
    }, 200);
  }, [globalF]);

  useEffect(() => {
    globalFilter !== globalF && setGlobalF(globalFilter);
  }, [globalFilter]);

  const parentTitle = allTabs[`${currentTab?.parent_id}`]?.title || '';
  const tabTitle = currentTab?.title || '';
  const currentPage = `${
    currentTab?.parent_id ? `${parentTitle} / ` : ''
  }${tabTitle}`;

  return (
    <Flex
      mx={theme.margin.map((m) => `-${m}`)}
      pl={theme.margin.map((m) => `calc(5px + ${m})`)}
      pr={theme.margin}
      py={theme.margin.map((m) => `calc(${m} / 2)`)}
      alignItems='center'
      flexWrap='wrap'
      zIndex={3}
      gap={[0, 0, 0, 3]}
      bgColor={theme.navBg}
      mb={3}
      boxShadow='lg'
      direction={['column', 'column', 'column', 'row']}
    >
      <Flex
        alignItems='center'
        gap={2}
        w={['100%', '100%', '100%', 'auto']}
        py={[2, 2, 2, 0]}
        flexGrow={[0, 0, 0, 1]}
      >
        {!isEditMode && <NormalIcon as={allIcons.HiBars3} onClick={onToggle} />}
        <Heading
          textTransform='capitalize'
          fontWeight='bold'
          fontSize={theme.fontSize.header}
          color={theme.main}
          whiteSpace='nowrap'
          textOverflow='ellipsis'
          overflow='hidden'
        >
          {currentPage}
        </Heading>
      </Flex>
      <Flex
        borderRadius='15px'
        bgColor={theme.none}
        alignItems='center'
        justifyContent='right'
        w={['100%', '100%', '100%', 'min-content']}
        m={0}
        py={1}
        gap={2}
      >
        {isAdmin && !isSuper && (
          <>
            <Text whiteSpace='nowrap' fontSize='sm'>
              Mode édition
            </Text>
            <Switch isChecked={isEditMode} onChange={handleMode} />
          </>
        )}
        {/* <NormalIcon
          as={allIcons.HiBars3}
          color={theme.secondary}
          onClick={onToggle}
          cursor='pointer'
          display={['block', 'none']}
        /> */}
        <InputGroup w={['100%', '100%', '100%', 'min-content']} ml={3}>
          <InputLeftElement
            children={
              <Icon
                as={allIcons.HiMagnifyingGlass}
                color={theme.secondary}
                cursor='pointer'
              />
            }
          />
          <Input
            variant='search'
            bg={'gray.50'}
            color={theme.main}
            _placeholder={{
              color: theme.secondary,
            }}
            borderRadius='7px'
            placeholder='Rechercher'
            onChange={handleFilterChange}
            value={globalF}
            transition='all 0.3s ease'
            w={['100%', '100%', '100%', '250px']}
            p={'auto'}
            mr={2}
          />
        </InputGroup>
        <Tooltip label='Filtres'>
          <span>
            <NormalIcon
              as={allIcons[filterActive ? 'HiFunnel' : 'HiOutlineFunnel']}
              onClick={() => toggleFilter()}
            />
          </span>
        </Tooltip>
        {!isEditMode &&
          !currentTab?.isPlanning &&
          tabToUpdate?._id &&
          (isAdmin || isSuper || canMulti) && (
            <Box
              position='relative'
              cursor='pointer'
              onClick={() => setModalType('runAction')}
            >
              <Tooltip label='Actions multiples'>
                <span>
                  <NormalIcon
                    disabled={!rowSelected.length}
                    as={allIcons.HiMiniWallet}
                    cursor='pointer'
                  />
                </span>
              </Tooltip>
              {rowSelected.length > 0 && (
                <Chip
                  nb={rowSelected.length}
                  bgColor='red.500'
                  color='white'
                  style={chipStyle}
                />
              )}
            </Box>
          )}
        <DownloadComponent />
        <ActionButtons />
        {!isMobile && (isAdmin || canUpload) && (
          <Tooltip label='Importer un fichier excel'>
            <span>
              <UploadComponent />
            </span>
          </Tooltip>
        )}
        {/* <Icon
          color={theme.secondary}
          as={allIcons[darkLight ? 'HiMiniMoon' : 'HiSun']}
          mx={2}
          onClick={() => setDarkLight((dl) => (dl ? 0 : 1))}
        /> */}
        {showActivity && canViewActivity && <ActivityLogs onClose={() => setShowActivity(false)} />}
        <Notifications />
        <Menu>
          <MenuButton mr={2}>
            <Icon
              display='block'
              as={allIcons.HiUserCircle}
              boxSize='40px'
              color={theme.dark}
            />
          </MenuButton>
          <MenuList>
            <MenuGroup
              title={`Connecté: ${login}`}
              fontSize={theme.fontSize.normal}
            />
            {/* <MenuGroup title='Options'> */}
            {/* <MenuItem>
                <Text flexGrow={1} onClick={toggleFilter}>
                  Avec filtres
                </Text>
                <Switch isChecked={filterActive} onChange={toggleFilter} />
              </MenuItem> */}
            {/* <MenuItem>
                <Text flexGrow={1} onClick={() => setShowNbLine((b) => !b)}>
                  Avec numero de ligne
                </Text>
                <Switch
                  isChecked={showNbLine}
                  onChange={() => setShowNbLine((b) => !b)}
                />
              </MenuItem> */}
            {/* </MenuGroup> */}
            <Divider my={3} />
            <MenuItem
              onClick={() =>
                setUser((u: any) => ({ ...u, tempPasswordForced: true }))
              }
            >
              Changer mon mot de passe
            </MenuItem>
            <MenuItem
              onClick={() => {
                subscribeUser();
              }}
            >
              Activer les notifications
            </MenuItem>
            <Divider my={3} />
            {canViewActivity && <MenuItem onClick={() => setShowActivity(true)}>Journal d’activité</MenuItem>}
            <MenuItem onClick={() => setToken('')}>Déconnexion</MenuItem>
          </MenuList>
        </Menu>
      </Flex>
    </Flex>
  );
};

export default Toolbar;
